import { useState, useEffect, useCallback, useRef } from 'react';
import { locationServiceV2 } from '../services/locationServiceV2';

/**
 * Custom hook for city-level location search with debouncing and caching
 * @param {Object} options - Configuration options
 * @param {number} options.debounceMs - Debounce delay in milliseconds (default: 300)
 * @param {number} options.minQueryLength - Minimum query length to trigger search (default: 2)
 * @returns {Object} Hook state and methods
 */
export const useLocationSearchV2 = (options = {}) => {
  const {
    debounceMs = 300,
    minQueryLength = 2
  } = options;

  // State management
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');

  // Refs for cleanup and rate limiting
  const debounceTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const lastSearchTimeRef = useRef(0);
  const requestCountRef = useRef(0);

  // Rate limiting configuration
  const RATE_LIMIT_WINDOW = 60000; // 1 minute
  const MAX_REQUESTS_PER_WINDOW = 30; // Max 30 requests per minute

  /**
   * Check if we're within rate limits
   */
  const isWithinRateLimit = useCallback(() => {
    const now = Date.now();
    const timeSinceLastWindow = now - lastSearchTimeRef.current;

    // Reset counter if window has passed
    if (timeSinceLastWindow > RATE_LIMIT_WINDOW) {
      requestCountRef.current = 0;
      lastSearchTimeRef.current = now;
    }

    return requestCountRef.current < MAX_REQUESTS_PER_WINDOW;
  }, []);

  /**
   * Perform the actual search
   */
  const performSearch = useCallback(async (searchQuery) => {
    // Check rate limiting
    if (!isWithinRateLimit()) {
      setError('Too many requests. Please wait a moment before searching again.');
      setLoading(false);
      return;
    }

    // Abort previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      setError(null);
      
      // Increment request counter
      requestCountRef.current += 1;

      const results = await locationServiceV2.searchCities(searchQuery);
      
      // Only update state if this request wasn't aborted
      if (!abortControllerRef.current.signal.aborted) {
        setSuggestions(results);
        setLoading(false);
      }
    } catch (err) {
      // Only update state if this request wasn't aborted
      if (!abortControllerRef.current.signal.aborted) {
        console.error('Location search error:', err);
        setError(err.message || 'Failed to search locations');
        setSuggestions([]);
        setLoading(false);
      }
    }
  }, [isWithinRateLimit]);

  /**
   * Debounced search function
   */
  const searchLocations = useCallback((searchQuery) => {
    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Update query state immediately for UI feedback
    setQuery(searchQuery);

    // Clear suggestions and error for empty or short queries
    if (!searchQuery || searchQuery.length < minQueryLength) {
      setSuggestions([]);
      setError(null);
      setLoading(false);
      return;
    }

    // Set loading state immediately
    setLoading(true);
    setError(null);

    // Debounce the actual search
    debounceTimeoutRef.current = setTimeout(() => {
      performSearch(searchQuery.trim());
    }, debounceMs);
  }, [debounceMs, minQueryLength, performSearch]);

  /**
   * Clear suggestions and reset state
   */
  const clearSuggestions = useCallback(() => {
    // Clear any pending timeouts
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Abort any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Reset state
    setSuggestions([]);
    setError(null);
    setLoading(false);
    setQuery('');
  }, []);

  /**
   * Reset error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Get detailed information for a selected location
   */
  const getLocationDetails = useCallback(async (placeId) => {
    try {
      setError(null);
      const details = await locationServiceV2.getCityDetails(placeId);
      return details;
    } catch (err) {
      console.error('Location details error:', err);
      setError(err.message || 'Failed to get location details');
      throw err;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Abort pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Return hook interface
  return {
    // State
    suggestions,
    loading,
    error,
    query,

    // Methods
    searchLocations,
    clearSuggestions,
    clearError,
    getLocationDetails,

    // Computed properties
    hasResults: suggestions.length > 0,
    isEmpty: suggestions.length === 0 && !loading && !error && query.length >= minQueryLength,
    isSearching: loading && query.length >= minQueryLength
  };
};

export default useLocationSearchV2;